import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { debounce } from '../../../utils/debounce'
import { useMenu } from '../../context/MenuContext'

const config = {
  hoverDelay: 500,
  minDistance: 50,
  minTimeSegments: 3, // Minimum number of time segments with movement required vs totalTimeSegments, so e.g. 3 out of 4 = 75%
  totalTimeSegments: 4,
}

export const useMenuHover = () => {
  const { openMenu, isMenuOpen, closeMenu, setScreenMenuOpen } = useMenu()
  const location = useLocation()

  const onClose = () => {
    closeMenu()
    setScreenMenuOpen(false)
  }

  const [pauseHover, setPauseHover] = useState(false)
  const changedPath = useRef(false)

  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null)
  const mouseMovementsRef = useRef<{ timestamp: number; x: number; y: number }[]>([])
  const lastMouseMoveRef = useRef<number>(0)

  const debouncedClearPause = debounce(() => {
    setPauseHover(false)
  }, 100)

  const debouncedRef = useRef(debouncedClearPause)
  debouncedRef.current = debouncedClearPause

  useEffect(() => {
    if (changedPath.current && isMenuOpen) {
      closeMenu()
      setScreenMenuOpen(false)
      setPauseHover(true)
    } else {
      changedPath.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, closeMenu, setScreenMenuOpen, setPauseHover])

  const hasEnoughMouseMovement = () => {
    const now = Date.now()
    const movements = mouseMovementsRef.current

    if (movements.length < 2) {
      return false
    }

    let totalDistance = 0
    for (let i = 1; i < movements.length; i++) {
      const deltaX = movements[i].x - movements[i - 1].x
      const deltaY = movements[i].y - movements[i - 1].y

      // Pythagoras to get distance between points
      totalDistance += Math.sqrt(deltaX * deltaX + deltaY * deltaY)
    }

    const timeSegments = Array(config.totalTimeSegments).fill(0)
    const periodStart = now - config.hoverDelay
    const segmentDuration = config.hoverDelay / config.totalTimeSegments

    // splitting the segments, so we get a percentage of movement needed to trigger opening
    movements.forEach((movement) => {
      const timeElapsed = movement.timestamp - periodStart
      if (timeElapsed >= 0) {
        const segmentIndex = Math.min(
          config.totalTimeSegments - 1,
          Math.floor(timeElapsed / segmentDuration)
        )
        timeSegments[segmentIndex]++
      }
    })

    const segmentsWithMovement = timeSegments.filter((count) => count > 0).length

    return totalDistance >= config.minDistance && segmentsWithMovement >= config.minTimeSegments
  }

  useEffect(() => {
    return () => {
      debouncedRef.current.cancel()
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current)
      }
      mouseMovementsRef.current = []
      lastMouseMoveRef.current = 0
    }
  }, [])

  const handleMouseEnter = () => {
    if (!pauseHover) {
      openMenu()
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    const now = Date.now()

    mouseMovementsRef.current.push({
      timestamp: now,
      x: e.clientX,
      y: e.clientY,
    })
    lastMouseMoveRef.current = now

    const trackingPeriodStart = now - config.hoverDelay
    mouseMovementsRef.current = mouseMovementsRef.current.filter(
      (movement) => movement.timestamp >= trackingPeriodStart
    )

    if (pauseHover && !hoverTimerRef.current) {
      hoverTimerRef.current = setTimeout(() => {
        if (hasEnoughMouseMovement()) {
          openMenu()
          setPauseHover(false)
        }
        hoverTimerRef.current = null
      }, config.hoverDelay)
    }
  }

  const handleMouseLeave = () => {
    onClose()

    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }

    mouseMovementsRef.current = []
    lastMouseMoveRef.current = 0

    if (pauseHover) {
      debouncedRef.current.cancel()
      setPauseHover(false)
    }
  }

  return {
    handlers: {
      onMouseEnter: handleMouseEnter,
      onMouseMove: handleMouseMove,
      onMouseLeave: handleMouseLeave,
    },
  }
}
